import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Resource.css";
import Table from "../../../components/reusable-table/Table";
import { DeleteIcon, EditIcon, SearchIcon } from "../../../icons/reactIcons";
import { useNavigate } from "react-router-dom";
import Input from "../../../components/reusable-input/Input";
import noImage from "../../../asstets/images/no_image.png";
import {
  deleteResourceThunk,
  fetchResourceCategoriesThunk,
  fetchResourceListThunk,
} from "../../../redux/slices/resourceSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { formatDateTime } from "../../../utils/formatDateTime";
import ConfirmUserActionModal from "../../../components/confirm-modal/ConfirmUserActionModal";

interface ResourceProps {
  onCreate: () => void;
  onEdit: (row: any) => void;
}

const Resource: React.FC<ResourceProps> = ({ onCreate, onEdit }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    categories,
    resources,
    loading,
    count: totalRecords,
  } = useAppSelector((state) => state.resource);

  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [tempCategories, setTempCategories] = useState<string[]>(["All"]);
  const [appliedCategories, setAppliedCategories] = useState<string[]>(["All"]);

  const [page, setPage] = useState(1);
  const limit = 15;

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(
    null,
  );
  const [selectedResourceTitle, setSelectedResourceTitle] =
    useState<string>("");

  const [submitting, setSubmitting] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* ================= API CALLS ================= */

  useEffect(() => {
    dispatch(fetchResourceCategoriesThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchResourceListThunk({
        page,
        limit,
      }),
    );
  }, [dispatch, page]);

  /* ================= CATEGORY LIST ================= */

  const categoryOptions = useMemo(() => {
    const apiCats = categories.map((c) => c.name);
    return ["All", ...apiCats];
  }, [categories]);

  /* ================= TABLE COLUMNS ================= */

  const columns = [
    { header: "Image", accessor: "image" },
    { header: "Title", accessor: "title" },
    { header: "Category", accessor: "category" },
    { header: "Created By", accessor: "createdBy" },
    { header: "Updated By", accessor: "updatedBy" },
  ];

  const actions = [
    {
      label: (
        <EditIcon color="black" style={{ width: "20px", height: "20px" }} />
      ),
      onClick: (row: any) => onEdit(row),
      className: "edit-btn",
    },
    {
      label: (
        <DeleteIcon color="black" style={{ width: "20px", height: "20px" }} />
      ),
      onClick: (row: any) => handleOpenDeleteModal(row),
      className: "delete-btn",
    },
  ];

  const handleToggle = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }

    setIsFilterOpen(!isFilterOpen);
  };

  const handleOpenDeleteModal = (row: any) => {
    setSelectedResourceId(row.id);
    setSelectedResourceTitle(row.title);
    setOpenDeleteModal(true);
  };

  const handleDeleteResource = async () => {
    if (!selectedResourceId) return;

    try {
      setSubmitting(true);

      const result = await dispatch(deleteResourceThunk(selectedResourceId));

      if (deleteResourceThunk.fulfilled.match(result)) {
        dispatch(fetchResourceListThunk({ page, limit }));
        setOpenDeleteModal(false);
        setSelectedResourceId(null);
      }
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= CATEGORY TOGGLE ================= */

  const toggleCategory = (cat: string) => {
    if (cat === "All") {
      setTempCategories(["All"]);
    } else {
      let newCats: string[] = [];

      if (tempCategories.includes("All")) {
        newCats = categoryOptions.filter((c) => c !== "All" && c !== cat);
      } else {
        newCats = tempCategories.includes(cat)
          ? tempCategories.filter((c) => c !== cat)
          : [...tempCategories, cat];

        const allExceptAll = categoryOptions.filter((c) => c !== "All");
        if (newCats.length === allExceptAll.length) {
          newCats = ["All"];
        }
      }

      setTempCategories(newCats);
    }
  };

  const handleApply = () => {
    setAppliedCategories(tempCategories);
    setIsFilterOpen(false);
  };

  const handleCancel = () => {
    setTempCategories(appliedCategories);
    setIsFilterOpen(false);
  };

  /* ================= FILTERED DATA ================= */

  const filteredResources = useMemo(() => {
    return resources.filter((item) => {
      const matchesSearch = item.name
        .toLowerCase()
        .includes(search.toLowerCase());

      const matchesCategory =
        appliedCategories.includes("All") ||
        appliedCategories.includes(item.categoryName);

      return matchesSearch && matchesCategory;
    });
  }, [resources, search, appliedCategories]);

  /* ================= FORMAT DATA FOR TABLE ================= */

  const tableData = filteredResources.map((item) => ({
    id: item.id,
    image: (
      <img
        src={item.fileUrl || noImage}
        alt={item.name}
        className="table-img"
        onError={(e) => {
          (e.target as HTMLImageElement).src = noImage;
        }}
      />
    ),
    title: item.name,
    category: item.categoryName ?? "-",
    createdBy: (
      <div className="merged-cell">
        <div className="merged-label">{item.creator?.name ?? "-"}</div>
        <div className="merged-date">{formatDateTime(item.createdAt)}</div>
      </div>
    ),
    updatedBy: (
      <div className="merged-cell">
        {!item.updater?.name ? (
          "-"
        ) : (
          <>
            <div className="merged-label">{item.updater?.name ?? "-"}</div>
            <div className="merged-date">
              {item.updatedAt ? formatDateTime(item.updatedAt) : "-"}
            </div>
          </>
        )}
      </div>
    ),
  }));

  /* ================= RENDER ================= */

  return (
    <div className="resource-page">
      <div className="resource-header">
        <h2 className="page-title">Resource</h2>

        <div className="resource-controls">
          <Input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            search
            startIcon={<SearchIcon />}
            removeBottomMargin
          />

          <div className="category-filter">
            <button
              ref={buttonRef}
              className="category-btn"
              onClick={handleToggle}
            >
              {appliedCategories.includes("All")
                ? "All Category ▾"
                : `${appliedCategories.join(", ")} ▾`}
            </button>

            {isFilterOpen && (
              <div
                ref={dropdownRef}
                className="category-dropdown"
                style={{
                  position: "fixed",
                  top: dropdownPos.top,
                  left: dropdownPos.left,
                }}
              >
                <div className="category-selection">
                  {categoryOptions.map((cat) => (
                    <label key={cat} className="category-option">
                      <input
                        type="checkbox"
                        checked={
                          tempCategories.includes("All")
                            ? true
                            : tempCategories.includes(cat)
                        }
                        onChange={() => toggleCategory(cat)}
                      />
                      {cat}
                    </label>
                  ))}
                </div>

                <div className="category-actions">
                  <button className="cancel-btn" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button className="apply-btn" onClick={handleApply}>
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          <button className="create-btn" onClick={onCreate}>
            Create New
          </button>
        </div>
      </div>

      <Table
        columns={columns}
        data={tableData}
        actions={actions}
        rowsPerPage={limit}
        loading={loading}
        serverSidePagination
        totalRecords={totalRecords || 0}
        onPageChange={(newPage) => setPage(newPage)}
        onRowClick={(row) => {
          navigate(`/resource-details/${row.id}`);
        }}
        rounded
      />

      {openDeleteModal && (
        <ConfirmUserActionModal
          type="delete"
          userName={selectedResourceTitle}
          loading={submitting}
          onClose={() => {
            setOpenDeleteModal(false);
            setSelectedResourceId(null);
          }}
          onConfirm={handleDeleteResource}
        />
      )}
    </div>
  );
};

export default Resource;
